import React from 'react';
import { Space, Typography } from 'antd';

const { Text } = Typography;

// Every row of a split renders at this height in both cells, so the mode/amount
// column and the UTR column line up split-for-split when read across.
const SPLIT_ROW_HEIGHT = 24;

const formatWholeAmount = (price) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price || 0);

/**
 * Normalise a payment-bearing record into a splits array.
 *
 * Payment records (SettlementPayment, AdvancePaymentLedger) carry `paymentSplits`
 * directly. Voucher records only store the collapsed `paymentMode` — literally
 * 'SPLIT' when the payment used several modes — so their splits have to be joined
 * in from the source payment; `sourcePayment` is that record when the caller has
 * it. Anything without splits at all is a legacy single-mode payment, which is
 * returned as a one-entry array so the cells are never blank.
 *
 * @param {object} record - the row (payment or voucher)
 * @param {object} [sourcePayment] - the payment behind a voucher row, if resolved
 * @returns {Array<{mode: string, amount: number, reference: string}>}
 */
export const resolveSplits = (record, sourcePayment) => {
  if (record?.paymentSplits?.length) return record.paymentSplits;
  if (sourcePayment?.paymentSplits?.length) return sourcePayment.paymentSplits;
  if (record?.paymentMode && record.paymentMode !== 'SPLIT') {
    return [{
      mode: record.paymentMode,
      amount: record.amount,
      reference: record.transactionReference || sourcePayment?.transactionReference || '',
      bankRail: '',
    }];
  }
  return [];
};

/** Modes that carry a rail — mirrors RAIL_MODES in models/shared/paymentSplitSchema. */
const RAIL_MODES = ['BANK_TRANSFER'];

/**
 * How a split should be labelled in a Mode column: the rail the approver recorded
 * (IMPS / NEFT) when there is one, otherwise the mode itself. Mirrors
 * `splitModeLabel` on the server so the screens and the printed voucher agree.
 */
export const splitModeLabel = (split) => {
  if (!split) return '';
  if (split.bankRail && RAIL_MODES.includes(split.mode)) return split.bankRail;
  return String(split.mode || '').replace(/_/g, ' ');
};

/**
 * The UTR / cheque number for one split. Splits carry their own reference; a
 * payment recorded before splits existed only has the one at payment level, so
 * fall back to it when there is a single split for it to belong to. With several
 * splits we do not guess which mode a payment-level reference belongs to.
 */
export const resolveSplitReference = (record, split, splitCount, sourcePayment) => {
  if (split?.reference) return split.reference;
  if (splitCount === 1) {
    return record?.transactionReference || sourcePayment?.transactionReference || '';
  }
  return '';
};

/**
 * Table cell: one line per split, each reading "UTR34678 - ₹23,992", plus a total
 * line when the payment was split across modes. This is the only cell needed to
 * read a split payment — there is no separate mode column.
 *
 * A split with no reference (cash, typically) shows its mode in place of the UTR,
 * so the line still says what the money was, e.g. "CASH - ₹50,000".
 *
 * `expectedTotal` (the row's own amount) is compared against the splits — for a
 * voucher row the two come from separate records, so a disagreement means the
 * voucher is stale and is worth surfacing.
 */
export const SplitReferenceCell = ({ record, splits, sourcePayment, expectedTotal, showTotal = true }) => {
  if (!splits?.length) return <Text type="secondary">—</Text>;

  const splitTotal = splits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  const mismatch = expectedTotal != null && Math.abs(splitTotal - (Number(expectedTotal) || 0)) > 0.5;

  return (
    <Space direction="vertical" size={2} style={{ width: '100%' }}>
      {splits.map((s, i) => {
        const reference = resolveSplitReference(record, s, splits.length, sourcePayment);
        const label = reference || splitModeLabel(s) || '—';
        // How the transfer was sent, shown alongside the UTR when the approver
        // recorded it — the same label the voucher prints in its Mode column.
        const rail = s.bankRail && RAIL_MODES.includes(s.mode) ? s.bankRail : '';
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, height: SPLIT_ROW_HEIGHT }}>
            {rail && reference && (
              <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{rail}</Text>
            )}
            <Text
              // Copyable only when there is a real reference to paste into a
              // bank statement; the mode fallback is not worth copying.
              copyable={reference ? { text: reference, tooltips: ['Copy UTR', 'Copied'] } : false}
              type={reference ? undefined : 'secondary'}
              style={{ fontSize: 12, fontFamily: reference ? 'monospace' : undefined, whiteSpace: 'nowrap' }}
              ellipsis={{ tooltip: `${label} - ${formatWholeAmount(s.amount)}` }}
            >
              {label}
            </Text>
            <Text strong style={{ whiteSpace: 'nowrap' }}>- {formatWholeAmount(s.amount)}</Text>
          </div>
        );
      })}
      {showTotal && splits.length > 1 && (
        <Text type={mismatch ? 'danger' : 'secondary'} style={{ fontSize: 11 }}>
          Total: {formatWholeAmount(splitTotal)}
          {mismatch ? ` (record: ${formatWholeAmount(expectedTotal)})` : ''}
        </Text>
      )}
    </Space>
  );
};

export default SplitReferenceCell;
